import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { AssistantConnection, AssistantOAuthProviderKey, AssistantSyncRun } from '@accounting/shared';
import {
  PROVIDER_LABELS,
  useAssistantStatus,
  useCalendars,
  useConnectCarries,
  useConnectProvider,
  useConnections,
  useDeleteConnection,
  useSyncNow,
  useSyncRuns,
  useUpdateCalendar,
  useUpdateConnection,
} from '../../hooks/useAssistant';
import { ApiError } from '../../lib/api';
import { useConnectImap } from '../../hooks/useAssistantMail';
import { FeedSettingsCard } from '../../components/assistant/FeedSettingsCard';

export const Route = createFileRoute('/_auth/assistant/connections')({ component: ConnectionsPage });

const OAUTH_PROVIDERS: { key: AssistantOAuthProviderKey; blurb: string; secretPrefix: string }[] = [
  { key: 'microsoft', blurb: 'Outlook calendar, mail and To Do. Connect each mailbox from either tenant.', secretPrefix: 'assistant-ms' },
  { key: 'google', blurb: 'Google Calendar and Tasks. Gmail comes separately, over IMAP.', secretPrefix: 'assistant-google' },
];

const INTERVALS: { sec: number; label: string }[] = [
  { sec: 3600, label: 'Every hour' },
  { sec: 10800, label: 'Every 3 hours' },
  { sec: 21600, label: 'Every 6 hours' },
  { sec: 43200, label: 'Twice a day' },
  { sec: 86400, label: 'Daily' },
];

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `Request failed (${err.status})`;
  }
  return err instanceof Error ? err.message : String(err);
}

function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function ConnectionsPage() {
  const status = useAssistantStatus();
  const connections = useConnections();
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  if (status.isLoading) {
    return (
      <div className="card" style={{ padding: 24 }}>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      <header>
        <h1 style={{ margin: 0 }}>Connections</h1>
        <p className="muted" style={{ marginTop: 4 }}>
          Every account the assistant reads from and writes to. Calendars pull every three hours by default; your own
          changes go out immediately.
        </p>
      </header>

      {message && (
        <div className={`card pa-banner pa-banner--${message.kind}`} role="status">
          <span>{message.text}</span>
          <button type="button" className="link-button" onClick={() => setMessage(null)}>
            Dismiss
          </button>
        </div>
      )}

      {!status.data?.configured && (
        <div className="card pa-banner pa-banner--warn">
          <div>
            <strong>The assistant is not configured yet.</strong> Add the provider secrets to Key Vault (see each card)
            and restart the API. Everything else on this page lights up once at least one provider is present.
          </div>
        </div>
      )}

      <section className="pa-provider-grid">
        {OAUTH_PROVIDERS.map((p) => (
          <ProviderCard
            key={p.key}
            provider={p}
            configured={!!status.data?.providers[p.key]?.configured}
            redirectUri={status.data?.redirectUri ?? null}
            onResult={setMessage}
          />
        ))}
        <ImapCard configured={!!status.data?.providers.imap?.configured} onResult={setMessage} />
        <CarriesCard configured={!!status.data?.providers.carries?.configured} onResult={setMessage} />
      </section>

      <section>
        <h2 style={{ marginBottom: 8 }}>Connected accounts</h2>
        {connections.isLoading && <p className="muted">Loading…</p>}
        {connections.data && connections.data.length === 0 && (
          <div className="card">
            <p className="muted" style={{ margin: 0 }}>
              Nothing connected yet. Use a card above to sign in to an account.
            </p>
          </div>
        )}
        {connections.data?.map((c) => (
          <ConnectionRow key={c.id} connection={c} onResult={setMessage} />
        ))}
      </section>

      <CalendarPicker />
      {status.data?.configured && <FeedSettingsCard onResult={setMessage} />}
    </div>
  );
}

function ProviderCard({
  provider,
  configured,
  redirectUri,
  onResult,
}: {
  provider: (typeof OAUTH_PROVIDERS)[number];
  configured: boolean;
  redirectUri: string | null;
  onResult: (m: { kind: 'ok' | 'err'; text: string }) => void;
}) {
  const connect = useConnectProvider();
  const [label, setLabel] = useState('');
  return (
    <div className={`card pa-provider-card${configured ? '' : ' pa-provider-card--muted'}`}>
      <h3>{PROVIDER_LABELS[provider.key]}</h3>
      <p className="muted">{provider.blurb}</p>
      {configured ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Label (optional), e.g. Law firm"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{ flex: 1, minWidth: 160 }}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={connect.isPending}
            onClick={() => {
              onResult({ kind: 'ok', text: `Opening ${PROVIDER_LABELS[provider.key]} sign-in…` });
              connect.mutate(
                { provider: provider.key, label: label.trim() || undefined },
                {
                  onSuccess: (r) => {
                    setLabel('');
                    onResult({
                      kind: 'ok',
                      text: `${r.isNew ? 'Connected' : 'Reconnected'} ${r.connection.accountEmail ?? r.connection.label}. First sync is running.`,
                    });
                  },
                  onError: (err) => onResult({ kind: 'err', text: errorMessage(err) }),
                },
              );
            }}
          >
            {connect.isPending ? 'Waiting for sign-in…' : 'Connect'}
          </button>
        </div>
      ) : (
        <p className="muted" style={{ fontSize: 13 }}>
          Not configured. Put <code>{provider.secretPrefix}-client-id</code> and <code>{provider.secretPrefix}-client-secret</code> in
          Key Vault{redirectUri ? <> and register the redirect URI <code>{redirectUri}</code></> : null}.
        </p>
      )}
    </div>
  );
}

function CarriesCard({ configured, onResult }: { configured: boolean; onResult: (m: { kind: 'ok' | 'err'; text: string }) => void }) {
  const connect = useConnectCarries();
  return (
    <div className={`card pa-provider-card${configured ? '' : ' pa-provider-card--muted'}`}>
      <h3>Carrie's Bar</h3>
      <p className="muted">The bar app's planner (counts, orders, closures, events) and its to-do list. Whole days only; edits go straight back to the bar app.</p>
      {configured ? (
        <button
          type="button"
          className="btn-primary"
          disabled={connect.isPending}
          onClick={() =>
            connect.mutate(
              {},
              {
                onSuccess: (r) => onResult({ kind: 'ok', text: `${r.isNew ? 'Connected' : 'Reconnected'} ${r.connection.label}. First sync is running.` }),
                onError: (err) => onResult({ kind: 'err', text: errorMessage(err) }),
              },
            )
          }
        >
          {connect.isPending ? 'Checking…' : 'Connect'}
        </button>
      ) : (
        <p className="muted" style={{ fontSize: 13 }}>Available once any provider above is configured.</p>
      )}
    </div>
  );
}

function ImapCard({ configured, onResult }: { configured: boolean; onResult: (m: { kind: 'ok' | 'err'; text: string }) => void }) {
  const connect = useConnectImap();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [label, setLabel] = useState('');
  const [other, setOther] = useState(false);
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(465);
  return (
    <div className={`card pa-provider-card${configured ? '' : ' pa-provider-card--muted'}`}>
      <h3>{other ? 'IMAP mailbox' : 'Gmail (IMAP)'}</h3>
      <p className="muted">
        {other
          ? 'Any mailbox with IMAP and SMTP (a web-host mailbox, for example). Mail only; no calendar or tasks.'
          : <>Mail for the attention feed, with a Google <strong>app password</strong> (needs 2-Step Verification on the account). Calendar and Tasks still come through the Google card.</>}
      </p>
      {configured ? (
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          onSubmit={(e) => {
            e.preventDefault();
            connect.mutate(
              {
                email: email.trim(),
                appPassword: other ? password : password.replace(/\s+/g, ''),
                label: label.trim() || undefined,
                imapHost: other ? imapHost.trim() : 'imap.gmail.com',
                imapPort: other ? imapPort : 993,
                smtpHost: other ? smtpHost.trim() : 'smtp.gmail.com',
                smtpPort: other ? smtpPort : 465,
              },
              {
                onSuccess: (r) => {
                  setPassword('');
                  onResult({ kind: 'ok', text: `${r.isNew ? 'Connected' : 'Updated'} ${r.connection.accountEmail ?? r.connection.label}. First sync is running.` });
                },
                onError: (err) => onResult({ kind: 'err', text: errorMessage(err) }),
              },
            );
          }}
        >
          <label className="pa-form__inline" style={{ fontSize: 13 }}>
            <input type="checkbox" checked={other} onChange={(e) => setOther(e.target.checked)} />
            Not Gmail — enter the server settings
          </label>
          <input type="email" placeholder={other ? 'name@yourdomain.com' : 'you@gmail.com'} value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
          <input type="password" placeholder={other ? 'Mailbox password' : '16-character app password'} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
          {other && (
            <div className="pa-form__row">
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                IMAP server (SSL)
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="text" placeholder="mail.yourdomain.com" value={imapHost} onChange={(e) => setImapHost(e.target.value)} required style={{ flex: 1 }} />
                  <input type="number" min={1} max={65535} value={imapPort} onChange={(e) => setImapPort(Number(e.target.value))} style={{ width: 80 }} />
                </div>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                SMTP server (SSL)
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="text" placeholder="mail.yourdomain.com" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} required style={{ flex: 1 }} />
                  <input type="number" min={1} max={65535} value={smtpPort} onChange={(e) => setSmtpPort(Number(e.target.value))} style={{ width: 80 }} />
                </div>
              </label>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} style={{ flex: 1 }} />
            <button type="submit" className="btn-primary" disabled={connect.isPending || !email || !password || (other && (!imapHost.trim() || !smtpHost.trim()))}>
              {connect.isPending ? 'Checking…' : 'Connect'}
            </button>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>The password is checked against the server, then stored encrypted. It is never shown again.</p>
        </form>
      ) : (
        <p className="muted" style={{ fontSize: 13 }}>Available once any provider above is configured.</p>
      )}
    </div>
  );
}

function SignatureEditor({ connection: c, busy }: { connection: AssistantConnection; busy: boolean }) {
  const update = useUpdateConnection();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(c.signature ?? '');
  const dirty = (text.trim() || null) !== (c.signature ?? null);
  return (
    <div style={{ marginTop: 6 }}>
      <button type="button" className="link-button" style={{ fontSize: 13 }} onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} Signature{c.signature ? '' : ' (none)'}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4, maxWidth: 520 }}>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={'Ken Bateman\nBateman Holdings LLC\n(816) 555-0100'} style={{ font: 'inherit', fontSize: 13 }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn-primary" disabled={busy || update.isPending || !dirty} onClick={() => update.mutate({ id: c.id, signature: text.trim() || null })}>
              {update.isPending ? 'Saving…' : 'Save signature'}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>Appended under a blank line to replies and new mail sent from this account. Plain text.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ConnectionRow({
  connection: c,
  onResult,
}: {
  connection: AssistantConnection;
  onResult: (m: { kind: 'ok' | 'err'; text: string }) => void;
}) {
  const update = useUpdateConnection();
  const remove = useDeleteConnection();
  const sync = useSyncNow();
  const reconnect = useConnectProvider();
  const [showRuns, setShowRuns] = useState(false);
  const runs = useSyncRuns(showRuns ? c.id : null);
  const busy = update.isPending || remove.isPending || sync.isPending || reconnect.isPending;

  return (
    <div className="card pa-connection">
      <div className="pa-connection__head">
        <span className="pa-dot" style={{ background: c.color }} aria-hidden="true" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong>{c.label}</strong>
            <span className="tag">{PROVIDER_LABELS[c.provider] ?? c.provider}</span>
            <StatusTag status={c.status} />
            {c.tenantId && (
              <span className="muted" style={{ fontSize: 12 }} title={c.tenantId}>
                tenant …{c.tenantId.slice(-6)}
              </span>
            )}
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            {c.accountEmail ?? '—'} · last synced {when(c.lastSyncedAt)} · next {when(c.nextSyncAt)}
          </div>
          {c.lastError && (
            <div className="field-error" style={{ fontSize: 13, marginTop: 4 }}>
              {c.lastError}
            </div>
          )}
          {c.capabilities.mail && <SignatureEditor connection={c} busy={busy} />}
        </div>
        <div className="pa-connection__actions">
          <select
            value={c.syncIntervalSec}
            disabled={busy}
            onChange={(e) => update.mutate({ id: c.id, syncIntervalSec: Number(e.target.value) })}
            title="How often to pull from this account"
          >
            {INTERVALS.map((i) => (
              <option key={i.sec} value={i.sec}>
                {i.label}
              </option>
            ))}
          </select>
          <input
            type="color"
            value={c.color}
            disabled={busy}
            onChange={(e) => update.mutate({ id: c.id, color: e.target.value })}
            title="Colour used for this account's events"
          />
          {c.status === 'needs_reauth' ? (
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() =>
                reconnect.mutate(
                  { provider: c.provider as AssistantOAuthProviderKey, reconnectConnectionId: c.id },
                  {
                    onSuccess: () => onResult({ kind: 'ok', text: `Reconnected ${c.label}.` }),
                    onError: (err) => onResult({ kind: 'err', text: errorMessage(err) }),
                  },
                )
              }
            >
              Reconnect
            </button>
          ) : (
            <button
              type="button"
              className="btn-secondary"
              disabled={busy || c.status === 'disabled'}
              onClick={() =>
                sync.mutate(c.id, {
                  onSuccess: (run) =>
                    onResult({
                      kind: run.status === 'ok' ? 'ok' : 'err',
                      text:
                        run.status === 'ok'
                          ? `Synced ${c.label}: ${run.stats['calendar.eventsUpserted'] ?? 0} events updated.`
                          : `Sync ${run.status}: ${run.error ?? 'see run history'}`,
                    }),
                  onError: (err) => onResult({ kind: 'err', text: errorMessage(err) }),
                })
              }
            >
              {sync.isPending ? 'Syncing…' : 'Sync now'}
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => {
              const next = window.prompt('Label for this connection', c.label);
              if (next && next.trim() && next.trim() !== c.label) update.mutate({ id: c.id, label: next.trim() });
            }}
          >
            Rename
          </button>
          {c.status !== 'needs_reauth' && (
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => update.mutate({ id: c.id, status: c.status === 'disabled' ? 'active' : 'disabled' })}
            >
              {c.status === 'disabled' ? 'Enable' : 'Pause'}
            </button>
          )}
          <button
            type="button"
            className="btn-danger"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`Remove ${c.label}? Its calendars and synced events are deleted from the assistant (not from ${PROVIDER_LABELS[c.provider]}).`)) {
                remove.mutate(c.id, { onError: (err) => onResult({ kind: 'err', text: errorMessage(err) }) });
              }
            }}
          >
            Remove
          </button>
          <button type="button" className="link-button" onClick={() => setShowRuns((s) => !s)}>
            {showRuns ? 'Hide history' : 'History'}
          </button>
        </div>
      </div>
      {showRuns && <RunHistory runs={runs.data ?? []} loading={runs.isLoading} />}
    </div>
  );
}

function StatusTag({ status }: { status: AssistantConnection['status'] }) {
  const label =
    status === 'active' ? 'Active' : status === 'needs_reauth' ? 'Needs sign-in' : status === 'disabled' ? 'Paused' : 'Error';
  return <span className={`tag pa-status pa-status--${status}`}>{label}</span>;
}

function RunHistory({ runs, loading }: { runs: AssistantSyncRun[]; loading: boolean }) {
  if (loading) return <p className="muted" style={{ margin: '8px 0 0' }}>Loading history…</p>;
  if (runs.length === 0) return <p className="muted" style={{ margin: '8px 0 0' }}>No syncs yet.</p>;
  return (
    <table className="table" style={{ marginTop: 10, fontSize: 13 }}>
      <thead>
        <tr>
          <th>Started</th>
          <th>Trigger</th>
          <th>Result</th>
          <th>Events</th>
          <th>Error</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((r) => (
          <tr key={r.id}>
            <td>{when(r.startedAt)}</td>
            <td>{r.trigger}</td>
            <td>
              <span className={`tag pa-run pa-run--${r.status}`}>{r.status}</span>
            </td>
            <td>
              {r.stats['calendar.eventsUpserted'] ?? 0} updated · {r.stats['calendar.eventsDeleted'] ?? 0} removed
            </td>
            <td className="muted">{r.error ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CalendarPicker() {
  const calendars = useCalendars();
  const update = useUpdateCalendar();
  if (!calendars.data || calendars.data.length === 0) return null;

  const byConnection = new Map<string, typeof calendars.data>();
  for (const cal of calendars.data) {
    const list = byConnection.get(cal.connectionId);
    if (list) list.push(cal);
    else byConnection.set(cal.connectionId, [cal]);
  }

  return (
    <section>
      <h2 style={{ marginBottom: 4 }}>Calendars</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Untick a calendar to keep it out of the unified view. Colour overrides the account colour for that calendar only.
      </p>
      <div className="card">
        {[...byConnection.entries()].map(([connId, cals]) => (
          <div key={connId} style={{ marginBottom: 12 }}>
            <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
              {cals[0]!.connectionLabel}
            </div>
            {cals.map((cal) => (
              <label key={cal.id} className="pa-calendar-row">
                <input
                  type="checkbox"
                  checked={cal.isSelected}
                  disabled={update.isPending}
                  onChange={(e) => update.mutate({ id: cal.id, isSelected: e.target.checked })}
                />
                <input
                  type="color"
                  value={cal.color}
                  disabled={update.isPending}
                  onChange={(e) => update.mutate({ id: cal.id, color: e.target.value })}
                  title="Colour for this calendar"
                />
                <span style={{ flex: 1 }}>{cal.name}</span>
                {cal.isPrimary && <span className="tag">primary</span>}
                {!cal.canWrite && <span className="tag">read-only</span>}
                {cal.timezone && <span className="muted" style={{ fontSize: 12 }}>{cal.timezone}</span>}
              </label>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
